import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { ActionWithPayload } from "@obg/ngrx-utils";
import { AppError, AuthenticationStatus, AuthSelector } from "@obg/ngx.core";
import { asyncScheduler, of } from "rxjs";
import { catchError, filter, map, observeOn, switchMap } from "rxjs/operators";

import { GAME_VARIANT_ACTION_TYPE } from "../game-variant/game-variant.action";
import { GetGameSettingsSuccessPayload, GetGameVariantSuccessPayload } from "../game-variant/game-variant.model";
import { GamingExternalState } from "../state";

import { USER_GAMES_ACTION_TYPE, UserGamesAction } from "./user-games.action";
import { UserGamesConfigService } from "./user-games.config";
import { GetAllLastPlayedParams } from "./user-games.model";
import { UserGamesService } from "./user-games.service";

@Injectable()
export class UserGamesEffect {
	authenticationChange$ = createEffect(() =>
		this.store.select(AuthSelector.getAuthenticationStatus).pipe(
			// auth state should be processed after all other effects of this class are registered
			observeOn(asyncScheduler),
			filter(
				(status) =>
					status === AuthenticationStatus.Authenticated || status === AuthenticationStatus.NotAuthenticated
			),
			map((status) => {
				if (status === AuthenticationStatus.Authenticated) {
					return this.action.getAllLastPlayed();
				}
				return this.action.reset();
			})
		)
	);

	allLastPlayed$ = createEffect(() =>
		this.actions$.pipe(
			ofType(USER_GAMES_ACTION_TYPE.getAllLastPlayed),
			map((x: ActionWithPayload<GetAllLastPlayedParams | undefined>) => x.payload),
			switchMap((params) =>
				this.service.getAllLastPlayed(params).pipe(
					map((response) => this.action.getAllLastPlayedSuccess(response)),
					catchError((error: AppError) => of(this.action.getAllLastPlayedFail(error)))
				)
			)
		)
	);

	gameVariantSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GAME_VARIANT_ACTION_TYPE.getSuccess),
			filter(
				(x: ActionWithPayload<GetGameVariantSuccessPayload>) =>
					!this.config.getGameIdsHiddenFromLastPlayed().includes(x.payload.gameVariant.gameId)
			),
			map((x: ActionWithPayload<GetGameVariantSuccessPayload>) =>
				this.action.updateLastPlayedGame(x.payload.gameVariant.gameId)
			)
		)
	);

	gameSettingsSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GAME_VARIANT_ACTION_TYPE.getSettingsSuccess),
			filter(
				(x: ActionWithPayload<GetGameSettingsSuccessPayload>) =>
					!this.config.getGameIdsHiddenFromLastPlayed().includes(x.payload.gameSettings.gameId)
			),
			map((x: ActionWithPayload<GetGameSettingsSuccessPayload>) =>
				this.action.updateLastPlayedGame(x.payload.gameSettings.gameId)
			)
		)
	);

	constructor(
		private actions$: Actions,
		private action: UserGamesAction,
		private service: UserGamesService,
		private store: Store<GamingExternalState>,
		private config: UserGamesConfigService
	) {}
}
